import { Node, Output } from 'rete'
import { BaseComponent } from './base_component'
import { NodeData, WorkerInputs, WorkerOutputs } from 'rete/types/core/data'
import { Extent } from 'ol/extent'
import { DateControl } from '../controls/date'
import { SelectControl } from '../controls/select'
import { TextControl } from '../controls/text'
import { PresetButtonsControl } from '../controls/preset_buttons'
import { booleanDataSocket, numericDataSocket } from '../socket_types'
import { BooleanTileGrid, NumericTileGrid, toIndex } from '../tile_grid'
import { createXYZ } from 'ol/tilegrid'
import { EPSG3857, EPSG4326, maskFromExtentAndShape } from '../bounding_box'
import * as proj4 from 'proj4'
import { ProjectProperties } from '.'

interface FirePoint {
    latitude: number
    longitude: number
    frp: number
}

const SENSORS = [
    { id: 0, name: 'All sensors', value: 'all' },
    { id: 1, name: 'VIIRS S-NPP', value: 'viirs_snpp' },
    { id: 2, name: 'VIIRS NOAA-20', value: 'viirs_noaa20' },
    { id: 3, name: 'VIIRS NOAA-21', value: 'viirs_noaa21' },
    { id: 4, name: 'MODIS Aqua/Terra', value: 'modis' },
] as const

const cache = new Map<string, FirePoint[]>()

function isoDate(d: Date): string {
    return d.toISOString().slice(0, 10)
}

function todayUtc(): string {
    return isoDate(new Date())
}

function parseIsoDate(value: unknown, fallback: string): string {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : fallback
}

function wgs84BboxFromExtent(extent: Extent): [number, number, number, number] {
    const sw = (proj4 as any).default(EPSG3857, EPSG4326, [extent[0], extent[1]])
    const ne = (proj4 as any).default(EPSG3857, EPSG4326, [extent[2], extent[3]])
    const west = Math.min(sw[0], ne[0])
    const south = Math.min(sw[1], ne[1])
    const east = Math.max(sw[0], ne[0])
    const north = Math.max(sw[1], ne[1])
    return [west, south, east, north]
}

function sensorValue(sensorId: unknown): typeof SENSORS[number]['value'] {
    const id = Number(sensorId ?? 0)
    return SENSORS.find(s => s.id === id)?.value ?? 'all'
}

function parseRadiusKm2(value: unknown): number {
    const n = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(n) || n <= 0) return 0
    return n
}

function paintFire(
    tx: number,
    ty: number,
    fire: FirePoint,
    presence: BooleanTileGrid,
    count: NumericTileGrid,
    frp: NumericTileGrid,
    mask: BooleanTileGrid
) {
    if (toIndex(presence, tx, ty) === undefined) return
    if (mask.get(tx, ty) !== true) return

    presence.set(tx, ty, true)
    const prevCount = count.get(tx, ty)
    count.set(tx, ty, isNaN(prevCount) ? 1 : prevCount + 1)
    const prevFrp = frp.get(tx, ty)
    const fireFrp = Number(fire.frp) || 0
    frp.set(tx, ty, isNaN(prevFrp) ? fireFrp : Math.max(prevFrp, fireFrp))
}

function forEachCellCoveredByFire(
    px: number,
    py: number,
    lat: number,
    radiusKm2: number,
    zoom: number,
    tileGrid: ReturnType<typeof createXYZ>,
    callback: (tx: number, ty: number) => void
) {
    const tileCoord = tileGrid.getTileCoordForCoordAndZ([px, py], zoom)
    const hitX = tileCoord[1]
    const hitY = tileCoord[2]
    callback(hitX, hitY)
    if (!(radiusKm2 > 0)) return

    const radiusM = Math.sqrt(radiusKm2 * 1_000_000 / Math.PI)
    const cosLat = Math.cos(lat * Math.PI / 180)
    const radius3857 = radiusM / Math.max(Math.abs(cosLat), 1e-6)
    const range = tileGrid.getTileRangeForExtentAndZ(
        [px - radius3857, py - radius3857, px + radius3857, py + radius3857],
        zoom
    )
    const r2 = radius3857 * radius3857
    for (let tx = range.minX; tx <= range.maxX; tx++) {
        for (let ty = range.minY; ty <= range.maxY; ty++) {
            if (tx === hitX && ty === hitY) continue
            const extent = tileGrid.getTileCoordExtent([zoom, tx, ty])
            const cx = (extent[0] + extent[2]) / 2
            const cy = (extent[1] + extent[3]) / 2
            const dx = cx - px
            const dy = cy - py
            if (dx * dx + dy * dy <= r2) callback(tx, ty)
        }
    }
}

async function fetchFires(
    extent: Extent,
    dateFrom: string,
    dateTo: string,
    sensor: string
): Promise<FirePoint[]> {
    const [west, south, east, north] = wgs84BboxFromExtent(extent)
    const cacheKey = [west.toFixed(5), south.toFixed(5), east.toFixed(5), north.toFixed(5), dateFrom, dateTo, sensor].join('|')
    const cached = cache.get(cacheKey)
    if (cached) return cached

    const params = new URLSearchParams({
        west: String(west),
        south: String(south),
        east: String(east),
        north: String(north),
        date_from: dateFrom,
        date_to: dateTo,
        sensor,
    })
    const response = await fetch(`/firms/fires?${params.toString()}`, { credentials: 'same-origin' })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
        throw new Error(typeof payload.error === 'string' ? payload.error : `FIRMS request failed (${response.status})`)
    }

    const fires = Array.isArray(payload.fires) ? payload.fires as FirePoint[] : []
    cache.set(cacheKey, fires)
    return fires
}

export class FirmsFireComponent extends BaseComponent {
    projectProps: ProjectProperties

    constructor(projectProps: ProjectProperties) {
        super('NASA FIRMS Fires')
        this.category = 'Global'
        this.projectProps = projectProps
    }

    async builder(node: Node) {
        node.meta.toolTip = 'NASA FIRMS satellite active-fire hotspots (MODIS and VIIRS) for the project area. Set a date range (GMT, up to 90 days). Radius is an area in km²: 0 paints a single grid cell; larger values expand each hotspot to a circle of that area. Each detection is a hotspot, not a mapped fire perimeter.'
        node.meta.toolTipLink = 'https://firms.modaps.eosdis.nasa.gov/'

        if (!node.data.DateFrom) node.data.DateFrom = todayUtc()
        if (!node.data.DateTo) node.data.DateTo = todayUtc()
        if (node.data.sensorId === undefined) node.data.sensorId = 0
        if (node.data.radius === undefined) node.data.radius = 0

        node.addControl(
            new SelectControl(
                this.editor,
                'sensorId',
                () => SENSORS.map(s => ({ id: s.id, name: s.name })),
                () => {},
                'Sensor',
                'after-above-io'
            )
        )
        node.addControl(
            new PresetButtonsControl(
                this.editor,
                'datePreset',
                'DateFrom',
                'DateTo',
                [
                    { id: 'today', label: 'Today', days: 1 },
                    { id: '3d', label: '3D', days: 3 },
                    { id: '7d', label: '7D', days: 7 },
                ]
            )
        )
        node.addControl(new DateControl(this.editor, 'DateFrom'))
        node.addControl(new DateControl(this.editor, 'DateTo'))
        node.addControl(new TextControl(this.editor, 'radius', 'Radius [[km²]]', '100%', 'AboveIoControl'))

        node.addOutput(new Output('presence', 'Fire presence', booleanDataSocket))
        node.addOutput(new Output('count', 'Fire count', numericDataSocket))
        node.addOutput(new Output('frp', 'FRP [[MW]]', numericDataSocket))
    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
        const editorNode = this.editor?.nodes.find(n => n.id === node.id)
        if (editorNode === undefined) return

        const fallback = todayUtc()
        const dateFrom = parseIsoDate(node.data.DateFrom, fallback)
        const dateTo = parseIsoDate(node.data.DateTo, fallback)
        if (dateFrom > dateTo) {
            editorNode.meta.errorMessage = 'Date from must be on or before date to.'
            editorNode.update()
            return
        }

        const mask = await maskFromExtentAndShape(this.projectProps)
        const tileGrid = createXYZ()
        const outputTileRange = tileGrid.getTileRangeForExtentAndZ(this.projectProps.extent, this.projectProps.zoom)
        const zoom = this.projectProps.zoom
        const minX = outputTileRange.minX
        const minY = outputTileRange.minY
        const width = outputTileRange.getWidth()
        const height = outputTileRange.getHeight()
        const radiusKm2 = parseRadiusKm2(node.data.radius)

        const presence = new BooleanTileGrid(zoom, minX, minY, width, height)
        const count = new NumericTileGrid(zoom, minX, minY, width, height)
        const frp = new NumericTileGrid(zoom, minX, minY, width, height)
        frp.properties.unit = 'MW'

        try {
            const fires = await fetchFires(this.projectProps.extent, dateFrom, dateTo, sensorValue(node.data.sensorId))
            delete editorNode.meta.errorMessage

            for (const fire of fires) {
                const [x, y] = (proj4 as any).default(EPSG4326, EPSG3857, [fire.longitude, fire.latitude])
                forEachCellCoveredByFire(x, y, fire.latitude, radiusKm2, zoom, tileGrid, (tx, ty) => {
                    paintFire(tx, ty, fire, presence, count, frp, mask)
                })
            }
        } catch (error) {
            editorNode.meta.errorMessage = error instanceof Error ? error.message : 'Failed to retrieve FIRMS fire data.'
        }

        editorNode.meta.output = outputs['presence'] = presence
        outputs['count'] = count
        outputs['frp'] = frp
        editorNode.update()
    }
}
